import React, { useCallback, useRef, useState } from 'react';
import { Box, Typography, Paper, Chip, Stack, Divider, TextField, Button } from '@mui/material';
import type { RunStageStep, RunStageGraphEdge, RunStageGraphVertex } from '@/types/run-stage.types';
import { StepTranslationRow } from '@/components/editor/RightPanel/TranslationPanel/StepTranslationRow';
import { buildStepTranslationFromEdge, buildStepTranslationFromVertex } from '@/run-stage/utils/buildExecuteStepTranslation';
import { executeUpdateEdgeProperties, executeUpdateVertexParameters } from '@services/runApi.service';

interface SubstepCardProps {
  step: RunStageStep;
  planStep?: RunStageStep;
  label: string;
  isReadOnly?: boolean;
  isOffline?: boolean;
  onTimingChange?: (stepId: string, field: 'startTime' | 'endTime', value: string) => void;
  /** Execute graph edge (actual state — editable if not completed) */
  actualEdge?: RunStageGraphEdge;
  /** Plan graph edge (original design-time state — always read-only) */
  definedEdge?: RunStageGraphEdge;
  /** Execute graph vertex for operationsInNodes runs (fallback when no edge) */
  actualVertex?: RunStageGraphVertex;
  /** Plan graph vertex for operationsInNodes runs (fallback when no edge) */
  definedVertex?: RunStageGraphVertex;
  /** Run ID for saving parameter changes */
  runId?: string;
  /** Called after a parameter edit is persisted */
  onParamSaved?: () => void;
}

function formatTime(time?: string | null): string {
  if (!time) return '—';
  return new Date(time).toLocaleString();
}

function toDatetimeLocal(time?: string | null): string {
  if (!time) return '';
  const d = new Date(time);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function getStatusColor(status?: string): 'default' | 'primary' | 'success' | 'warning' | 'error' {
  switch (status?.toUpperCase()) {
    case 'COMPLETED': return 'success';
    case 'IN_PROGRESS': return 'primary';
    case 'SKIPPED': return 'warning';
    case 'ABORTED': return 'error';
    default: return 'default';
  }
}

/**
 * Renders a single execute step with:
 * - Status + timing header
 * - "Actual Step" translation (editable StepTranslationRow, from execute graph edge)
 * - "Defined Step" translation (read-only StepTranslationRow, from plan graph edge)
 * - Timing history + machine info footer
 */
export const SubstepCard: React.FC<SubstepCardProps> = ({
  step,
  planStep,
  label,
  isReadOnly = false,
  isOffline = false,
  onTimingChange,
  actualEdge,
  definedEdge,
  actualVertex,
  definedVertex,
  runId,
  onParamSaved,
}) => {
  const [localStart, setLocalStart] = useState(() => toDatetimeLocal(step.realTime?.startTime));
  const [localEnd, setLocalEnd] = useState(() => toDatetimeLocal(step.realTime?.endTime));

  // Dirty param overrides for the actual step (accumulate without immediate save)
  const [dirtyParams, setDirtyParams] = useState<Record<string, unknown>>({});
  const [isSaving, setIsSaving] = useState(false);

  const showTimingInputs = isOffline && !isReadOnly;
  const isEditable = !isReadOnly && !step.completed;
  const hasDirtyParams = Object.keys(dirtyParams).length > 0;

  // Merge dirty params into edge for live rendering (v1 edge-based runs)
  const effectiveActualEdge: RunStageGraphEdge | undefined = actualEdge
    ? Object.keys(dirtyParams).length === 0
      ? actualEdge
      : {
          ...actualEdge,
          properties: {
            ...actualEdge.properties,
            value: {
              ...(actualEdge.properties?.value ?? {}),
              parameters: {
                ...((actualEdge.properties?.value?.parameters ?? {}) as Record<string, unknown>),
                ...dirtyParams,
              },
            },
          },
        }
    : undefined;

  // Merge dirty params into vertex for live rendering (v2 vertex-based runs)
  const effectiveActualVertex: typeof actualVertex = actualVertex
    ? Object.keys(dirtyParams).length === 0
      ? actualVertex
      : {
          ...actualVertex,
          parameters: {
            ...(actualVertex.parameters ?? {}),
            ...dirtyParams,
          },
        }
    : undefined;

  const handleParameterChanged = useCallback(
    (_nodeId: string, paramName: string, value: unknown) => {
      if (!isEditable) return;
      // Handle dotted param names for nested fields (e.g., "Temperature.from", "Temperature.to")
      if (paramName.includes('.')) {
        const [parentKey, childKey] = paramName.split('.', 2);
        setDirtyParams((prev) => {
          const existing = (prev[parentKey] ?? actualVertex?.parameters?.[parentKey] ?? {}) as Record<string, unknown>;
          return {
            ...prev,
            [parentKey]: { ...existing, [childKey]: value },
          };
        });
      } else {
        setDirtyParams((prev) => ({
          ...prev,
          [paramName]: value,
        }));
      }
    },
    [isEditable, actualVertex],
  );

  // Save all dirty parameters at once
  const handleMarkCompleted = useCallback(async () => {
    if (!hasDirtyParams || !runId) return;
    
    setIsSaving(true);
    try {
      if (actualEdge) {
        // v1 edge-based run
        const updatedProperties = {
          ...actualEdge.properties,
          value: {
            ...((actualEdge.properties?.value ?? {}) as Record<string, unknown>),
            parameters: {
              ...((actualEdge.properties?.value?.parameters ?? {}) as Record<string, unknown>),
              ...dirtyParams,
            },
          },
        };
        await executeUpdateEdgeProperties({
          runId,
          stepId: actualEdge.edgeId,
          edgeDocId: actualEdge._id,
          cloneNumber: actualEdge.cloneNumber ?? 1,
          properties: updatedProperties,
        });
      } else if (actualVertex) {
        // v2 vertex-based run
        await executeUpdateVertexParameters({
          runId,
          vertexId: actualVertex.vertexId,
          cloneNumber: actualVertex.cloneNumber ?? 1,
          parameters: dirtyParams,
        });
      }
      setDirtyParams({});
      onParamSaved?.();
    } catch (err) {
      console.error('Failed to save parameters:', err);
    } finally {
      setIsSaving(false);
    }
  }, [dirtyParams, runId, actualEdge, actualVertex, hasDirtyParams, onParamSaved]);

  const handleStartChange = (value: string) => {
    setLocalStart(value);
    if (value) onTimingChange?.(step.stepId, 'startTime', new Date(value).toISOString());
  };

  const handleEndChange = (value: string) => {
    setLocalEnd(value);
    if (value) onTimingChange?.(step.stepId, 'endTime', new Date(value).toISOString());
  };

  // Build translations
  const edgeToShow = effectiveActualEdge ?? definedEdge;
  const actualTranslation = edgeToShow
    ? buildStepTranslationFromEdge(edgeToShow, label, isEditable, step.stepName ?? step.stepId)
    : effectiveActualVertex
      ? buildStepTranslationFromVertex(effectiveActualVertex, label, isEditable, step.stepName ?? step.stepId)
      : definedVertex
        ? buildStepTranslationFromVertex(definedVertex, label, isEditable, step.stepName ?? step.stepId)
        : null;

  const definedTranslation =
    definedEdge && definedEdge !== actualEdge
      ? buildStepTranslationFromEdge(definedEdge, label, false, planStep?.stepName ?? step.stepName ?? step.stepId)
      : definedVertex && definedVertex !== actualVertex
        ? buildStepTranslationFromVertex(definedVertex, label, false, planStep?.stepName ?? step.stepName ?? step.stepId)
        : null;

  return (
    <Paper variant="outlined" sx={{ mb: 1.5, overflow: 'hidden' }}>
      {/* Status header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 2,
          py: 0.75,
          backgroundColor: 'grey.50',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {label}. {step.stepName || step.stepId}
        </Typography>
        <Chip
          label={step.status || 'PENDING'}
          color={getStatusColor(step.status)}
          size="small"
          sx={{ height: 20 }}
        />
        <Box sx={{ flex: 1 }} />
        {!showTimingInputs && (
          <>
            <Typography variant="caption" color="text.secondary">
              {formatTime(step.realTime?.startTime)}
            </Typography>
            {step.realTime?.endTime && (
              <>
                <Typography variant="caption" color="text.secondary">→</Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatTime(step.realTime.endTime)}
                </Typography>
              </>
            )}
          </>
        )}
      </Box>

      <Box sx={{ p: 0 }}>
        {/* Offline timing inputs */}
        {showTimingInputs && (
          <Box sx={{ px: 2, pt: 2 }}>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <TextField
                label="Start Time"
                type="datetime-local"
                size="small"
                value={localStart}
                onChange={(e) => handleStartChange(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ flex: 1 }}
              />
              <TextField
                label="End Time"
                type="datetime-local"
                size="small"
                value={localEnd}
                onChange={(e) => handleEndChange(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ flex: 1 }}
              />
            </Stack>
          </Box>
        )}

        {/* ── Actual Step ── */}
        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main' }}>
              Actual Step
            </Typography>
            {hasDirtyParams && (
              <Button
                size="small"
                variant="contained"
                color="primary"
                disabled={isSaving}
                onClick={handleMarkCompleted}
                sx={{ textTransform: 'none' }}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            )}
          </Box>
          {actualTranslation ? (
            <StepTranslationRow
              translation={actualTranslation}
              highlighted={false}
              editable={isEditable}
              selectable={false}
              onStepClicked={() => {}}
              onParameterChanged={handleParameterChanged}
              onCheckpointChanged={() => {}}
              onIconClicked={() => {}}
            />
          ) : (
            <Box sx={{ px: 2, pb: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                {step.stepName || step.stepId}
                {step.subType ? ` — ${step.subType}` : ''}
              </Typography>
              {step.notes && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic', mt: 0.5 }}>
                  {step.notes}
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {/* ── Defined Step (plan) — shown when params diverge from design ── */}
        {(definedTranslation || planStep) && (hasDirtyParams || definedTranslation) && (
          <Box>
            <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                Defined Step
              </Typography>
            </Box>
            {definedTranslation ? (
              <Box sx={{ opacity: 0.75 }}>
                <StepTranslationRow
                  translation={definedTranslation}
                  highlighted={false}
                  editable={false}
                  selectable={false}
                  onStepClicked={() => {}}
                  onParameterChanged={() => {}}
                  onCheckpointChanged={() => {}}
                  onIconClicked={() => {}}
                />
              </Box>
            ) : planStep ? (
              <Box sx={{ px: 2, pb: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {planStep.stepName || planStep.stepId}
                  {planStep.subType ? ` — ${planStep.subType}` : ''}
                </Typography>
              </Box>
            ) : null}
          </Box>
        )}

        {/* Timing history */}
        {step.timings && step.timings.length > 0 && (
          <Box sx={{ px: 2, pb: 1.5 }}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              Timing History
            </Typography>
            <Stack spacing={0.25} sx={{ mt: 0.5 }}>
              {step.timings.map((t, i) => (
                <Typography key={i} variant="caption" color="text.secondary">
                  {formatTime(t.startTime)} → {formatTime(t.endTime)}
                </Typography>
              ))}
            </Stack>
          </Box>
        )}

        {/* Machine info */}
        {step.machines && step.machines.length > 0 && (
          <Box sx={{ px: 2, pb: 1.5 }}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              Instruments
            </Typography>
            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
              {step.machines.map((m, i) => (
                <Typography key={i} variant="body2">
                  {m.name || m.machineId || '—'}
                  {m.machineUID ? ` (${m.machineUID})` : ''}
                </Typography>
              ))}
            </Stack>
          </Box>
        )}

        {step.actualQtyUsed?.amount != null && (
          <Box sx={{ px: 2, pb: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              Qty used: {step.actualQtyUsed.amount} {step.actualQtyUsed.units ?? ''}
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};
